import { useMemo, useRef, type CSSProperties } from 'react';

import { Button, FlexAligner } from '@scaleaq/scaleui';
import { InventoryProvider, MappingCollections } from '@scaleaq/sitecontrol-lib';
import { Images } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Content } from '@/components/cardWithThreeFooterBox';
import { GridWidget } from '@/components/GridWidget.tsx';
import { LiceCountConfig, useLiceCountChart } from '@/components/liceCountWidget/';
import { OrbitOneImageGalleryModal } from '@/components/orbitOneImageGalleryModal/OrbitOneImageGalleryModal.tsx';
import { OrbitOneGalleryContext } from '@/components/orbitOneImageGalleryModal/orbitOneImageGalleryModal/index.ts';
import { Z_INDEX } from '@/constants';
import { WindowSize } from '@/dataAccess/rest/measurement/measurement.types.ts';
import { useDimensions, useLayoutProps, useOpenModal } from '@/hooks';
import { widthPxToGridColumns } from '@/pages/landingPage/grid/widgetConstraints.ts';
import { useConfigStore } from '@/stores';
import { ComponentType, GenericWidgetProps } from '@/types.ts';
import { Icon, responsive } from '@/utils';
import { LICE_COUNT_COMPONENT_NAME } from '@/widgetComponentNames.ts';

import { getTwoColumnWidgetLayout } from '../utils/twoColumnWidgetLayout/getTwoColumnWidgetLayout.ts';
import { ChartAggregationResolution } from './chart';
import { useDataFromConfig } from './chart/chart/aggregatedChartCard/aggregatedHighChart/useDataFromConfig.ts';
import { AggregatedHighChartPreData } from './chart/chart/aggregatedChartCard/AggregatedHighChartPreData.tsx';
import { LiceCountModal } from './liceCountWidget/LiceCountModal.tsx';
import { LICE_COUNT_TWO_COLUMN_WIDGET_LAYOUT } from './liceCountWidget/liceCountTwoColumnWidgetLayout.ts';
import { cutIfNoData } from './liceCountWidget/liceCountWidget.functions.ts';
import { LiceCountWidgetContentWithData } from './liceCountWidget/LiceCountWidgetContentWithData.tsx';

const defaultSize = { w: 18, h: 30 };
// Per-day low-data-capture floor from Mechatronics. Each chart point is a 3-day
// window (mean/std/SE/count over three consecutive days), so the indicator uses 90.
const liceCountLowDataCaptureThreshold = 90;

const liceNormalizeTimeSeries = [
  MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountAdultFemale],
  MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountMobile],
  MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountCaligus],
  MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountAdultFemaleEggString],
  MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountAdultFemaleNoEggString],
];

interface LiceCountProps extends GenericWidgetProps {
  config?: LiceCountConfig;
  editable?: boolean;
  widgetId: string;
  style?: CSSProperties;
  isStandalone?: boolean;
}

// Moving-average width the API applies to the daily series. The chart's span is separate (see
// `getLiceChartTimeRange`), so the rows and chart show a three-day average over seven days.
const movingAverageWindow = WindowSize.ThreeDays;

export const LiceCountWidget: ComponentType<LiceCountProps> = ({
  config: configInput,
  editable = true,
  widgetId,
  style,
  isStandalone = false,
  ...rest
}: LiceCountProps) => {
  const { t } = useTranslation(['common', 'orbitOne']);

  const portableMode = useConfigStore(s => s.portableMode);
  const galleryModalController = useOpenModal();
  const modalController = useOpenModal();

  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useDimensions(containerRef, 100);

  const { config, size = defaultSize } = useLayoutProps<LiceCountConfig>({
    configInput,
    defaultConfig: { id: '', name: '' },
    widgetId,
  });

  // Width stays 0 until the container is measured. Treating that as columns collapses the chart.
  const effectiveColumns = isStandalone && width > 0 ? widthPxToGridColumns(width) : size.w;

  const { shouldFirstChildRender, shouldSecondChildRender, firstChildFlex, secondChildFlex } =
    getTwoColumnWidgetLayout({
      ...LICE_COUNT_TWO_COLUMN_WIDGET_LAYOUT,
      numberOfColumnsInWidget: effectiveColumns,
    });

  const { editId } = useMemo(() => ({ editId: uuidv4() }), []);

  const toolbarActionList = useMemo(
    () =>
      editable
        ? [
            {
              id: editId,
              label: t('common:edit'),
              onClick: modalController.handleOpenModal,
              Icon: Icon.SlidersHorizontal,
            },
          ]
        : [],
    [editId, editable, modalController.handleOpenModal, t]
  );

  const { chartConfig } = useLiceCountChart({
    windowSize: movingAverageWindow,
    inventoryId: config.id,
  });

  // One shared measurement-ranges fetch for both the summary rows and the chart (five plot types
  // × one sensor; the two egg-string series start hidden on the chart). Avoids the old split
  // where `LiceCountWidgetContent` issued three more requests via `useLiceCount` while
  // `AggregatedHighChart` fetched the same series again.
  const normalizeTimeSeries =
    chartConfig.resolution === ChartAggregationResolution.DAILY
      ? liceNormalizeTimeSeries
      : undefined;
  const { isLive, isTimeSeriesNormalized, data } = useDataFromConfig({
    config: chartConfig,
    normalizeTimeSeries,
  });
  const { allMeasurements, isLoading, isError, isFetched, isRateLimited, seriesStatus } = data;
  // The request end is the open day. Pull the axis back only when that day has no reading, so an
  // empty today is hidden and a partial day stays visible.
  const openDayCut = useMemo(
    () => cutIfNoData({ measurements: allMeasurements, cutoff: chartConfig.to }),
    [allMeasurements, chartConfig.to]
  );
  const axisConfig = useMemo(
    () => ({
      ...chartConfig,
      to: chartConfig.to?.minus({ days: openDayCut }),
    }),
    [chartConfig, openDayCut]
  );
  const hasMeasurements = useMemo(
    () => Object.values(allMeasurements).flatMap(plot => Object.values(plot)).length !== 0,
    [allMeasurements]
  );
  const isEmpty = isFetched && !hasMeasurements;

  return (
    <FlexAligner fullHeight ref={containerRef}>
      <GridWidget
        {...rest}
        headerTitle={t('orbitOne:liceCount.title', { sensorName: config.name })}
        id={widgetId}
        isEmpty={isEmpty}
        isError={isError}
        isLoading={isLoading}
        isRateLimited={isRateLimited}
        cloudBacked={true}
        style={{ ...style }}
        toolbarActionList={toolbarActionList}
      >
        <FlexAligner
          direction={'row'}
          style={{
            flex: 1,
            minHeight: 0,
            alignSelf: 'stretch',
            padding: `${responsive.spacing['3xlarge']} ${responsive.spacing['2xlarge']} 0`,
          }}
        >
          {shouldFirstChildRender && (
            <Content $flex={firstChildFlex}>
              <FlexAligner
                direction={'column'}
                gap={responsive.spacing['xlarge']}
                fullHeight
                spaceBetween={portableMode}
                style={portableMode ? { paddingBottom: responsive.spacing['6xlarge'] } : undefined}
              >
                <LiceCountWidgetContentWithData
                  windowSize={movingAverageWindow}
                  id={config.id}
                  allMeasurements={allMeasurements}
                  seriesStatus={seriesStatus}
                />
                {portableMode && (
                  <Button
                    style={{ zIndex: Z_INDEX.overlay.error }}
                    variant={'ghost'}
                    size={'small'}
                    headIcon={<Images />}
                    label={t('orbitOne:liceCount.imageGallery.action')}
                    onClick={galleryModalController.handleOpenModal}
                  />
                )}
              </FlexAligner>
            </Content>
          )}
          {shouldSecondChildRender && (
            <Content $flex={secondChildFlex} style={{ minHeight: 0, overflow: 'hidden' }}>
              <AggregatedHighChartPreData
                allMeasurements={allMeasurements}
                hasMeasurements={hasMeasurements}
                isLive={isLive}
                isTimeSeriesNormalized={isTimeSeriesNormalized}
                config={axisConfig}
                yAxisOptions={{
                  tickInterval: 0.5,
                  minorTickInterval: 0.25,
                  minRange: 1,
                  floor: 0,
                  // Default 5% headroom can cross the next 0.5 tick, so a series that fits in
                  // 0–2.5 is drawn on a 0–3 axis. No padding keeps the top gridline on the data.
                  maxPadding: 0,
                }}
                lowDataCaptureThreshold={liceCountLowDataCaptureThreshold}
              />
            </Content>
          )}
        </FlexAligner>
      </GridWidget>
      <LiceCountModal widgetId={widgetId} modalController={modalController} />
      <OrbitOneImageGalleryModal
        inventoryId={config.id}
        galleryContext={OrbitOneGalleryContext.Lice}
        modalController={galleryModalController}
      />
    </FlexAligner>
  );
};

// Static Property
LiceCountWidget.componentName = LICE_COUNT_COMPONENT_NAME;
